import { FilterKeys, Filters, Option, PageType } from '../pageConfig/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { BackendComparisonPeriodTypeMapping, RelativePeriod } from '@components/comparison-period-selector/Types';
import { Features, PageRequest, ProductsParam, ProductsReq, SaleType, volumeString } from '@models/Products';
import { formatProductsPeriod, getProductsMonthlyPeriod, getWeeklyPeriod } from '../PeriodUtils';

import {
  ProductDataType,
  ProductsItemFeaturesRequestPayload,
  ProductsParamArgumentObject,
  ProductType,
  RequestPayloadArgumentObject,
} from '@pages/product-analyzer/Product';
import { isValidTimeFrameAndPeriod } from '../FilterUtils';
import { cloneDeep } from 'lodash';
import { FocusArea } from '@components/products-top-5-table/Types';
import { getCurrencyCode } from '@utils/currencyUtils/CurrencyUtils';

/**
 * Formats the filter options into an array of their values.
 *
 * @param filter - The filter object containing options.
 * @returns An array of option values.
 */
export const formatParam = (filter: Filters): string[] => {
  return filter.options.map((option) => {
    return option.value;
  });
};

/**
 * Applies brand filter parameters to the request object.
 * Sets exclusion flags and brand values if applicable.
 *
 * @param req - The request object to modify.
 * @param filter - The filter object to process.
 */
export const applyBrandFilterParams = (req: any, filter: Filters): void => {
  if (filter.filterKey !== FilterKeys.BRAND) {
    return;
  }

  if (filter.isExclusion) {
    req.excludedBrand = formatParam(filter);
    return;
  }

  req.brand = formatParam(filter);
};

const addExportComparisonPeriodType = (requestPayload: RequestPayloadArgumentObject, req: ProductsReq): void => {
  // Currently, only the products compare modal export endpoint in the backend consumes the
  // comparisonPeriodType value in the request body, to determine the comparison period when
  // calculating the KPIs for the rolling year calculations.
  const selectedComparisonPeriodType = requestPayload?.exportCompareComparisonPeriodType;
  if (selectedComparisonPeriodType) {
    req.comparisonPeriodType = BackendComparisonPeriodTypeMapping[selectedComparisonPeriodType as RelativePeriod];
  }
};

export const constructRequest = (requestPayloadArgumentObject: RequestPayloadArgumentObject): ProductsReq => {
  const req = {} as ProductsReq;
  requestPayloadArgumentObject.period = formatProductsPeriod(
    requestPayloadArgumentObject.period,
    requestPayloadArgumentObject.periodType,
    true,
  );

  const periodMeta =
    requestPayloadArgumentObject.periodType === Periodicity.Monthly
      ? getProductsMonthlyPeriod(requestPayloadArgumentObject.period, requestPayloadArgumentObject.comparisonPeriod)
      : getWeeklyPeriod(requestPayloadArgumentObject.period, requestPayloadArgumentObject.comparisonPeriod);

  req.clientId = requestPayloadArgumentObject.clientId;
  req.productType = requestPayloadArgumentObject.productType;
  req.productDataType = ProductDataType.ProductAnalyzer;
  req.country = requestPayloadArgumentObject.countryId;
  req.kpi = requestPayloadArgumentObject.kpi;
  req.calculateRollingPeriod = requestPayloadArgumentObject.calculateRollingPeriod;

  req.itemIdList = requestPayloadArgumentObject.itemIdList;

  if (requestPayloadArgumentObject.productGroup) {
    req.productGroup = requestPayloadArgumentObject.productGroup.value;
  }

  req.periodType = requestPayloadArgumentObject.periodType ? requestPayloadArgumentObject.periodType.toUpperCase() : '';

  req.period = periodMeta
    ? formatProductsPeriod(periodMeta.currentPeriod, requestPayloadArgumentObject.periodType).toUpperCase()
    : '';
  req.comparisonPeriod = periodMeta
    ? formatProductsPeriod(periodMeta.comparisonPeriod, requestPayloadArgumentObject.periodType).toUpperCase()
    : '';

  addExportComparisonPeriodType(requestPayloadArgumentObject, req);

  let features: Features[] = [];

  requestPayloadArgumentObject.appliedFilters.forEach((filter) => {
    switch (filter.filterKey) {
      case FilterKeys.AVERAGE_PRICE_MIN:
        req.minPrice = formatParam(filter)[0] ?? '';
        break;
      case FilterKeys.AVERAGE_PRICE_MAX:
        req.maxPrice = formatParam(filter)[0] ?? '';
        break;
      case FilterKeys.REGION:
        req.region = formatParam(filter);
        break;
      case FilterKeys.CHANNEL:
        req.channel = formatParam(filter);
        break;
      case FilterKeys.ONLINE_OFFLINE:
        req.distType = formatParam(filter);
        break;
      case FilterKeys.FIRST_ACTIVITY_START_DATE:
        req.startDate = formatParam(filter)[0] ?? '';
        break;
      case FilterKeys.FIRST_ACTIVITY_END_DATE:
        req.endDate = formatParam(filter)[0] ?? '';
        break;
      case FilterKeys.CITY:
        req.city = formatParam(filter);
        break;
      case FilterKeys.BRAND:
        applyBrandFilterParams(req, filter);
        break;
      case FilterKeys.ADDFEAT:
        if (filter.options.length > 0) features.push({ name: filter.filterTitle, value: formatParam(filter) });
        break;
      case FilterKeys.CURRENCY:
        req.targetCurrency = getCurrencyCode(filter);
        break;
    }
  });

  if (features.length > 0) {
    req.features = features;
  }
  //Pagination use
  paginationValuePreparation(requestPayloadArgumentObject, req);

  //Sorting use
  req.isMarket = requestPayloadArgumentObject.isMarket;
  req.sortBy = requestPayloadArgumentObject.sortBy;
  req.sortDirection = requestPayloadArgumentObject.sortDirection;
  req.sortKey = requestPayloadArgumentObject.sortKey;
  comparisonGrowthPreparation(requestPayloadArgumentObject, req);
  req.productGrouping = requestPayloadArgumentObject.productGrouping;
  return req;
};

/**
 * Applies common filter parameters to the request object based on the filter key.
 * Modifies the request object with appropriate values for each filter type.
 * @param req - The request object to update.
 * @param filter - The filter object containing filterKey and options.
 */
export const applyCommonFilterParamsToRequest = (req: any, filter: Filters): void => {
  switch (filter.filterKey) {
    case FilterKeys.ONLINE_OFFLINE:
      req.split = formatParam(filter);
      break;
    case FilterKeys.CHANNEL:
      req.channel = formatParam(filter);
      break;
    case FilterKeys.REGION:
      req.region = formatParam(filter);
      break;
    case FilterKeys.CITY:
      req.city = formatParam(filter);
      break;
    case FilterKeys.BRAND:
      applyBrandFilterParams(req, filter);
      break;
    case FilterKeys.PRICEBAND:
      req.priceBand = formatParam(filter);
      break;
    case FilterKeys.SEGFEAT:
      req.segmentFeature = formatParam(filter);
      break;
    case FilterKeys.ADDFEAT:
      req.feature = filter.filterTitle;
      req.featureValue = formatParam(filter);
      break;
    case FilterKeys.CURRENCY:
      req.targetCurrency = getCurrencyCode(filter);
      break;
  }
};

export const paginationValuePreparation = (
  requestPayloadArgumentObject: RequestPayloadArgumentObject,
  req: ProductsReq,
) => {
  //Pagination use
  if (requestPayloadArgumentObject.page) {
    req.page = requestPayloadArgumentObject.page;
  }
  if (requestPayloadArgumentObject.size) {
    req.size = requestPayloadArgumentObject.size;
  }
};

const comparisonGrowthPreparation = (requestPayloadArgumentObject: RequestPayloadArgumentObject, req: ProductsReq) => {
  req.comparisonGrowth = requestPayloadArgumentObject.comparisonGrowth
    ? requestPayloadArgumentObject.comparisonGrowth.toUpperCase()
    : requestPayloadArgumentObject.comparisonGrowth;
};

export const constructProductsParam = (productsParamObject: ProductsParamArgumentObject): ProductsParam | undefined => {
  const appliedFilters = [...productsParamObject.pagesConfig.appliedFilters[PageType.RI_PRODUCT].selectedFilters];
  // checks if local period state is empty or not. If empty, use the redux state
  let selectedPeriodLabel = productsParamObject.period.label;
  const selectedPeriodValue = productsParamObject.period.value;
  if (!selectedPeriodLabel || selectedPeriodLabel === '') {
    selectedPeriodLabel = productsParamObject.mainFilterStateProducts.period.label;
  }

  // checks on selectedPeriodValue to avoid calling API when the value is "" or undefined
  const copyPeriod = cloneDeep(productsParamObject.period);
  copyPeriod.label = selectedPeriodLabel;
  if (!isValidTimeFrameAndPeriod(productsParamObject.timeFrame, copyPeriod)) {
    return;
  }

  // kpi should always be aligned with the headerKey
  let kpi = SaleType.revenue;
  if (productsParamObject.pagesConfig.sortingOptionsProducts.headerKey.includes(volumeString)) {
    kpi = SaleType.volume;
  }

  // Special handling for comparison when selectedPeriodValue is YTD or MAT
  let comparisonPeriod: string = isYTDOrMATForComparisonPeriod(
    selectedPeriodValue,
    productsParamObject.comparisonPeriod as RelativePeriod,
  );

  let isMarket = false;
  if (isMarketValidation(productsParamObject)) {
    isMarket = true;
  }

  // Special handling for Trends Tab only to inject kpi value into KPI payload.
  if (isProductTrendsTab(productsParamObject)) {
    kpi = productsParamObject.selectedSaleType;
  }

  return {
    productType: productsParamObject.productType,
    clientId: productsParamObject.retailerClientState.clientId,
    countryId: productsParamObject.countriesToQuery[0]?.id,
    productGroup: productsParamObject.productGroupParam,
    periodType: productsParamObject.timeFrame,
    period: selectedPeriodValue,
    comparisonPeriod: comparisonPeriod,
    pageRequest: {
      page: productsParamObject.pageNumber,
      size: productsParamObject.defaultPageSize,
    } as PageRequest,
    sortOption: productsParamObject.pagesConfig.sortingOptionsProducts,
    appliedFilters: appliedFilters,
    kpi: productsParamObject.overrideKpi ?? kpi,
    sortingKpi: productsParamObject.sortingKpi,
    isMarket: isMarket,
    growthType: productsParamObject.growthType,
    itemIdList: productsParamObject.itemIdList,
    groupDetails: productsParamObject.productGrouping,
  };
};

export const constructRequestForProductsItemFeatures = (
  clientId: number,
  productType: ProductType,
  countryId: string,
  productGroup: Option | undefined,
  periodType: Periodicity,
  itemIds: string[],
): ProductsItemFeaturesRequestPayload => {
  return {
    clientId,
    productType,
    productDataType: ProductType.ProductAnalyzer,
    country: [countryId],
    productGroup: productGroup?.value,
    periodType: periodType.toUpperCase(),
    itemIds: itemIds,
  };
};

export const isYTDOrMATForComparisonPeriod = (
  selectedPeriodValue: string,
  selectedComparisonPeriod: RelativePeriod = RelativePeriod.PreviousYearCurrentPeriod,
) => {
  let comparisonPeriod: string = selectedComparisonPeriod;

  if ('YTD' === selectedPeriodValue) {
    comparisonPeriod = 'YTD LY';
  }

  if ('MAT' === selectedPeriodValue) {
    comparisonPeriod = 'MAT LY';
  }

  return comparisonPeriod;
};

//Sorting Kpi only available from product trends tab, the rest all undefined.
export const isProductTrendsTab = (productsParamObject: ProductsParamArgumentObject) => {
  return productsParamObject.sortingKpi;
};

export const isMarketValidation = (productsParamObject: ProductsParamArgumentObject) => {
  return productsParamObject.isMarket && productsParamObject.isMarket === FocusArea.market;
};
